import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import Footer from "../components/Footer";
import ContactCTA from "../components/ContactCTA";
import SolutionDescription from "../components/SolutionDescription";
import SolutionFeatures from "../components/SolutionFeatures";
import { Phone, Radio, Wifi, Users, Shield, ShieldCheck, Bug, Laptop, ArrowLeft } from "lucide-react";

const solutions = [
  {
    icon: Phone,
    key: "skydial",
    color: "text-textBlue",
    bgColor: "bg-blueLight",
    features: ["onpremise", "cloud", "hybrid", "branch", "sip"],
    hasCategories: true,
    isSkydial: true,
  },
  {
    icon: Radio,
    key: "voiceapi",
    color: "text-secondaryOrange",
    bgColor: "bg-orange-50",
    features: ["integration", "scalable", "reliable"],
  },
  {
    icon: Users,
    key: "aicounting",
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    features: {
      benefits: ["traffic", "optimize", "marketing", "experience", "efficiency"],
      analytics: ["age", "gender", "employee", "reid", "accuracy"],
      behavioral: ["dwell", "flow", "anonymous", "realtime"],
    },
    hasCategories: true,
  },
  {
    icon: Shield,
    key: "vms",
    color: "text-emerald-600",
    bgColor: "bg-emerald-50",
    features: {
      main: ["efficient", "professional", "compliance", "secure"],
      sectors: ["corporate", "government", "hospital", "education"],
    },
    hasCategories: true,
    hasSectors: true,
  },
  {
    icon: ShieldCheck,
    key: "soc",
    color: "text-red-600",
    bgColor: "bg-red-50",
    features: {
      main: ["visibility", "monitoring", "ai", "soccompliance", "sovereignty", "multitenancy"],
      architecture: ["collection", "normalization", "correlation", "storage", "alerting", "reporting"],
      logsources: ["network", "os", "identity", "soccloud", "endpoint", "applications"],
      usecases: ["abnormal", "privilege", "compromised", "ransomware", "insider", "massdownload", "multisite"],
      industries: ["manufacturing", "logistics", "healthcare", "government", "financial", "retail", "socprofessional"],
    },
    hasCategories: true,
    isSOC: true,
  },
  {
    icon: Bug,
    key: "vapt",
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    features: {
      main: ["autonomous", "agentic", "onprem", "supervised", "boxmodes", "vaptreporting"],
      stages: ["upload", "assessment", "pentest", "report"],
      agents: ["orchestrator", "recon", "scanner", "exploit", "vaptreporter"],
      coverage: ["systemva", "mobile", "api", "server", "appvapt", "ad"],
      testingmodes: ["blackbox", "greybox", "whitebox"],
      impact: ["discovery", "turnaround", "toolchain", "reporttime"],
      trust: ["humanloop", "precision", "pilot"],
    },
    hasCategories: true,
    isVAPT: true,
  },
  {
    icon: Laptop,
    key: "firstprotect",
    color: "text-indigo-600",
    bgColor: "bg-indigo-50",
    features: {
      main: ["fpprevention", "fpondevice", "fpsigma", "fpcrossplatform", "fpuserspace", "fpmodules"],
      agentprotect: ["fpidentity", "fpenforcement", "fpforensics"],
      protectmodules: ["fpdata", "fpshell", "fpcredential", "fpapp", "fproot", "fpdestructive"],
      deployment: ["fpzerotouch", "fpdataplane", "fpauditmode", "fpoffline", "fpsoc", "fpalertquality"],
      proof: ["fpexfil", "fpoffline", "fpearlier"],
      stats: ["fplatency", "fpefficacy", "fpcpu", "fpplatforms"],
    },
    hasCategories: true,
    isFirstProtect: true,
  },
  {
    icon: Wifi,
    key: "networking",
    color: "text-textBlueDark",
    bgColor: "bg-backgroundTeal",
    features: ["bandwidth", "latency", "rural"],
  },
];

export default function SolutionDetail() {
  const { t } = useTranslation();
  const { solutionKey } = useParams();

  const solution = solutions.find((s) => s.key === solutionKey);

  usePageMeta({
    title: solution ? `${t(`solutions.${solution.key}.title`)} | Sky Global` : "Solutions | Sky Global",
    description: solution ? t(`solutions.${solution.key}.subtitle`) : t("solutions.title"),
    path: `/solutions/${solutionKey}`,
  });

  if (!solution) {
    return (
      <>
        <div className="pt-40 min-h-[60vh] flex items-center justify-center px-6">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-black mb-4">{t("notFound.unknownSolution")}</h1>
            <Link
              to="/solutions"
              className="text-textBlue hover:text-textBlueDark font-semibold no-underline transition-colors"
            >
              {t("notFound.backSolutions")}
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const Icon = solution.icon;

  return (
    <>
      <div className="bg-white">
        {/* Header */}
        <section className="relative overflow-hidden page-header pt-40 tablet:pt-36 mobile:pt-32 pb-20 tablet:pb-16 px-6 mobile:px-4">
          <div
            className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_center,#000_25%,transparent_70%)]"
            aria-hidden="true"
          ></div>
          <div className="relative container-page">
            <Link
              to="/solutions"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-textBlue hover:text-textBlueDark no-underline transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              {t("solutions.label")}
            </Link>
            <div className="mb-5">
              <span className={`${solution.bgColor} w-12 h-12 rounded-xl flex items-center justify-center`}>
                {solution.key === "skydial" ? (
                  <img
                    src="/logo skydial.svg"
                    alt=""
                    width="28"
                    height="28"
                    className="w-7 h-7 object-contain"
                  />
                ) : (
                  <Icon className={`w-6 h-6 ${solution.color}`} aria-hidden="true" />
                )}
              </span>
            </div>
            <h1 className="text-5xl tablet:text-4xl mobile:text-3xl font-semibold tracking-[-0.02em] text-black leading-[1.05] max-w-3xl mb-3">
              {t(`solutions.${solution.key}.title`)}
            </h1>
            {t(`solutions.${solution.key}.subtitle`, { defaultValue: "" }) && (
              <p className="text-lg tablet:text-base text-gray-700 leading-relaxed max-w-2xl">
                {t(`solutions.${solution.key}.subtitle`)}
              </p>
            )}
          </div>
        </section>

        {/* Description & Image Section */}
        <SolutionDescription solutionKey={solution.key} />

        {/* Features Section */}
        <SolutionFeatures solution={solution} />

        <ContactCTA />
        <Footer />
      </div>
    </>
  );
}
